import { QrPayload, ScanResultState, ScanValidationResponse } from './types';

/**
 * Parse a raw QR string into the expected ticket payload. Returns null when the string isn't valid
 * JSON or lacks the required fields, so every call site gets one consistent failure path instead of a
 * thrown exception. (The final cast is sound — the three fields are type-checked just above it.)
 */
export const parseQrPayload = (raw: string): QrPayload | null => {
	try {
		const parsed = JSON.parse(raw);
		if (
			typeof parsed?.user_id !== 'number' ||
			typeof parsed?.code !== 'string' ||
			typeof parsed?.ticketing_mode !== 'string'
		) {
			return null;
		}
		return parsed as QrPayload;
	} catch {
		return null;
	}
};

/**
 * Map a validation response to the discriminated UI state that drives `PunchupScanResult`. Both the QR path
 * (`validateEncryptedTicket`) and the manual path (`validateTicketsManually`) return the same shape and key off
 * `errors[]`, so resolving the verdict in one place keeps the two flows identical.
 */
export const buildScanResult = (data: ScanValidationResponse): ScanResultState =>
	data.errors && data.errors.length > 0 ? { type: 'error', errors: data.errors } : { type: 'success', data };

/** Best-effort human message from an axios/validation error, without leaking the raw error object. Shared by
 * the QR and manual validate flows so a request failure surfaces the same way in both. */
export const extractValidationError = (error: unknown): string | null => {
	if (error && typeof error === 'object') {
		const data = (error as { response?: { data?: { message?: string; error?: unknown } } }).response?.data;
		if (data?.message) return data.message;
		if (typeof data?.error === 'string' && data.error.trim().length > 0) return data.error;
		const message = (error as { message?: unknown }).message;
		if (typeof message === 'string' && message.trim().length > 0) return message;
	}
	return null;
};
